import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { formatDate, formatNumber } from '../../../services/inventoryService';

/**
 * DeleteCountsDialog - Confirmation modal for deleting physical counts
 * Shows what will be deleted and requires typing "DELETE" to confirm
 */
function DeleteCountsDialog({
  open,
  onClose,
  onConfirm,
  counts = [],
  loading = false,
  // DT-1931: the parent's delete error. The parent keeps this dialog open when the delete fails and sets its page
  // error, which renders behind this modal, so the dialog shows it too.
  error = null
}) {
  const [confirmText, setConfirmText] = useState('');

  // DT-1931: start every opening with an empty box, but keep it after a failed delete (the dialog stays open with
  // the error) so a retry does not need DELETE typed again.
  useEffect(() => {
    if (open) setConfirmText('');
  }, [open]);

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  const handleConfirm = () => {
    if (confirmText === 'DELETE') {
      onConfirm(counts.map(c => c.id));
    }
  };

  // Calculate totals
  const totalCounts = counts.length;
  const totalItems = counts.reduce((sum, c) => sum + (parseInt(c.total_items) || 0), 0);
  const totalAdjustments = counts.reduce((sum, c) => sum + (parseInt(c.adjustments_count) || 0), 0);

  const isConfirmEnabled = confirmText === 'DELETE' && !loading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningIcon />
        Delete Physical Counts?
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            You are about to delete <strong>{totalCounts}</strong> physical count(s):
          </Typography>
        </Box>

        {/* Counts to be deleted */}
        <TableContainer sx={{ maxHeight: 300, mb: 3 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Count #</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Items</TableCell>
                <TableCell align="right">Adjustments</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {counts.map((count) => (
                <TableRow key={count.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {count.count_number || `PC-${count.id}`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={count.warehouse_code} size="small" />
                  </TableCell>
                  <TableCell>{formatDate(count.count_date)}</TableCell>
                  <TableCell align="right">{formatNumber(count.total_items || 0)}</TableCell>
                  <TableCell align="right">{formatNumber(count.adjustments_count || 0)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Warning box */}
        <Alert
          severity="error"
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            This will PERMANENTLY delete:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>{totalCounts} physical count record{totalCounts !== 1 ? 's' : ''}</li>
            <li>{formatNumber(totalItems)} count item record{totalItems !== 1 ? 's' : ''}</li>
            <li>{formatNumber(totalAdjustments)} inventory adjustment record{totalAdjustments !== 1 ? 's' : ''}</li>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Stock levels will be recalculated without these counts.
          </Typography>
        </Alert>

        {/* Confirmation input */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Type <strong>DELETE</strong> to confirm:
          </Typography>
          <TextField
            size="small"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder="Type DELETE"
            fullWidth
            autoFocus
            error={confirmText.length > 0 && confirmText !== 'DELETE'}
            helperText={confirmText.length > 0 && confirmText !== 'DELETE' ? 'Please type DELETE exactly' : ''}
            disabled={loading}
            sx={{ maxWidth: 300 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={!isConfirmEnabled}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
        >
          {loading ? 'Deleting...' : 'Delete Permanently'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteCountsDialog;
